import { Briefcase, ChevronDown } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { naceCodeToPath } from "~/lib/directory-utils";
import type { CompanyProfile, NaceSecondaryEntry } from "~/types/company";
import { OverviewCard } from "./card";

const COLLAPSED_COUNT = 3;

function NaceChip({ code, description }: { code: string | null; description: string | null }) {
	const label = [code, description].filter(Boolean).join(" — ");
	if (!label) return null;

	if (code) {
		return (
			<Link
				to={`/directory/odvetvi/${naceCodeToPath(code)}`}
				className="inline-flex items-center rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700 hover:bg-primary-100 transition-colors"
			>
				{label}
			</Link>
		);
	}
	return (
		<span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
			{label}
		</span>
	);
}

/** ⑥ Předmět podnikání a činnosti — collapsed activity list + NACE chips. */
export function ActivitiesCard({ company }: { company: CompanyProfile }) {
	const [expanded, setExpanded] = useState(false);

	const activities = company.predmetPodnikani;
	const secondary: NaceSecondaryEntry[] = company.naceSecondary ?? [];
	const hasNace = Boolean(company.naceCode || company.naceDescription) || secondary.length > 0;

	if (activities.length === 0 && !hasNace) return null;

	const visible = expanded ? activities : activities.slice(0, COLLAPSED_COUNT);

	return (
		<OverviewCard hue="amber" icon={Briefcase} title="Předmět podnikání a činnosti">
			{activities.length > 0 && (
				<ul className="flex flex-col gap-1.5 text-sm">
					{visible.map((activity) => (
						<li key={activity} className="flex items-baseline gap-2">
							<span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gray-400" aria-hidden />
							{activity}
						</li>
					))}
				</ul>
			)}
			{activities.length > COLLAPSED_COUNT && (
				<button
					type="button"
					onClick={() => setExpanded(!expanded)}
					className="mt-2 flex items-center gap-1.5 text-sm text-primary hover:underline"
				>
					<ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
					{expanded ? "Zobrazit méně" : `Zobrazit všech ${activities.length}`}
				</button>
			)}

			{hasNace && (
				<div className={activities.length > 0 ? "mt-4 border-t border-gray-100 pt-4" : ""}>
					<p className="text-xs font-medium text-muted-foreground mb-2">Klasifikace NACE</p>
					<div className="flex flex-wrap gap-2">
						{(company.naceCode || company.naceDescription) && (
							<NaceChip code={company.naceCode} description={company.naceDescription} />
						)}
						{secondary.map((entry) => (
							<NaceChip key={entry.code} code={entry.code} description={entry.description} />
						))}
					</div>
				</div>
			)}
		</OverviewCard>
	);
}
